import { useState } from 'react';
import type { Movie } from '@filmsloth/shared';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { useSession } from '../session/useSession.js';

/**
 * "Wrong trailer?" — one tap, no dialog, no reason to pick.
 *
 * The trailer disappears for this session straight away, which is what the
 * group actually wants; whether it comes down for everybody depends on other
 * groups saying the same, and the confirmation says so rather than promising
 * more than one session can decide. Nothing here is undoable, which is fine:
 * the worst case is a card showing its generated cover, exactly as it did
 * before anybody added a trailer.
 */
export function TrailerReportButton({ movie }: { movie: Movie }) {
  const t = useT();
  const { credentials } = useSession();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  if (!credentials) return null;

  // The card re-renders without the player as soon as the session state comes
  // back, so this note is the only trace the button leaves behind.
  if (note) return <p className="trailer__reported small muted">{note}</p>;

  const report = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const outcome = await api.reportTrailer(credentials.token, movie.id);
      if (outcome.removed) setNote(t('trailerReport.removed'));
      else if (outcome.keptForReview) setNote(t('trailerReport.hidden'));
      else setNote(t('trailerReport.counted'));
    } catch {
      // The session only hides the trailer once the report is counted, so a
      // refusal here really did leave everything as it was.
      setNote(t('trailerReport.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="btn btn--ghost small trailer__report"
      onClick={report}
      disabled={busy}
    >
      {busy ? t('trailerReport.busy') : t('trailerReport.action')}
    </button>
  );
}
